import { Themes } from './themes';
import { ViewModeSettings } from './view-mode-settings';
import { Languages } from '../models/languages';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from '../../appconfig';
import { SettingKeys } from './setting-keys';
import { RBCheck, ViewModelProperty, VmpSelectionType } from '@lfw/roesberg';
import { Subject, Subscription } from 'rxjs';
import { KeyValuePair } from '@lfw/roesberg.liveforms.core';
import { SearchSettings } from './search-settings';
import { HelpSettings } from './help-settings';
import { OrderSettings } from './order-settings';

export class Settings {
  public viewMode: ViewModeSettings = new ViewModeSettings();
  public orderSettings: OrderSettings = new OrderSettings();
  public themes: Themes = new Themes();
  public helpSettings: HelpSettings = new HelpSettings();
  public searchSettings: SearchSettings = new SearchSettings();
  public languages: Languages;
  public settingsList: ViewModelProperty[] = [];

  public settingsChanged: Subject<KeyValuePair> = new Subject<KeyValuePair>();

  public visibleSettings: Array<Array<ViewModelProperty>> = [];

  subscriptions: Subscription[] = [];

  private viewSettingFields: ViewModelProperty[] = [];
  private orderSettingFields: ViewModelProperty[] = [];
  private pdfSettingFields: ViewModelProperty[] = [];
  private pdfEditorSettingFields: ViewModelProperty[] = [];
  private searchSettingFields: ViewModelProperty[] = [];
  private miscSettingFields: ViewModelProperty[] = [];

  constructor(
    private config: AppConfig,
    private translate: TranslateService
  ) {
    this.languages = new Languages(this.config, this.translate);
  }

  public parseAndApplySettingsList() {
    if (this.settingsList == null || this.settingsList.length == 0) {
      return;
    }

    this.cleanUp();

    this.settingsList.forEach((item) => {
      this.applySettingItem(item, true);
    });

    if (this.viewSettingFields.length > 0) {
      // set group names
      this.viewSettingFields.forEach((item) => {
        item.subGroupName = 'SETTINGS.UIHEADER';
        this.subscriptions.push(
          item.valueChange.subscribe(() => {
            this.valueChanged(item);
          })
        );
      });
      // add fields by order
      this.visibleSettings.push(this.viewSettingFields.sort((a, b) => a.order - b.order));
    }

    if (this.orderSettingFields.length > 0) {
      // set group names
      this.orderSettingFields.forEach((item) => {
        item.subGroupName = 'SETTINGS.ORDERHEADER';
        this.subscriptions.push(
          item.valueChange.subscribe(() => {
            this.valueChanged(item);
          })
        );
      });
      // add fields by order
      this.visibleSettings.push(this.orderSettingFields.sort((a, b) => a.order - b.order));
    }

    if (this.pdfSettingFields.length > 0) {
      this.pdfSettingFields.forEach((item) => {
        item.subGroupName = 'SETTINGS.PDFHEADER';
        this.subscriptions.push(
          item.valueChange.subscribe(() => {
            this.valueChanged(item);
          })
        );
      });
      // add fields by order
      this.visibleSettings.push(this.pdfSettingFields.sort((a, b) => a.order - b.order));
    }

    if (this.pdfEditorSettingFields.length > 0) {
      this.pdfEditorSettingFields.forEach((item) => {
        item.subGroupName = 'SETTINGS.PDFEDITORHEADER';
        this.subscriptions.push(
          item.valueChange.subscribe(() => {
            this.valueChanged(item);
          })
        );
      });
      // add fields by order
      this.visibleSettings.push(this.pdfEditorSettingFields.sort((a, b) => a.order - b.order));
    }

    if (this.searchSettingFields.length > 0) {
      this.searchSettingFields.forEach((item) => {
        item.subGroupName = 'SETTINGS.SEARCHHEADER';
        this.subscriptions.push(
          item.valueChange.subscribe(() => {
            this.valueChanged(item);
          })
        );
      });
      // add fields by order
      this.visibleSettings.push(this.searchSettingFields.sort((a, b) => a.order - b.order));
    }

    if (this.miscSettingFields.length > 0) {
      this.miscSettingFields.forEach((item) => {
        item.subGroupName = 'SETTINGS.MISCHEADER';
        this.subscriptions.push(
          item.valueChange.subscribe(() => {
            this.valueChanged(item);
          })
        );
      });
      // add fields by order
      this.visibleSettings.push(this.miscSettingFields.sort((a, b) => a.order - b.order));
    }

    this.themes.applyTheme();
    this.languages.applyLanguage();
  }

  cleanUp() {
    if (!RBCheck.isNullOrHasNoValues(this.subscriptions)) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }

    this.subscriptions = [];
    this.visibleSettings = [];
    this.viewSettingFields = [];
    this.orderSettingFields = [];
    this.pdfSettingFields = [];
    this.pdfEditorSettingFields = [];
    this.searchSettingFields = [];
    this.miscSettingFields = [];
  }

  public applySettingItem(item: ViewModelProperty, configure = false) {
    if (item.value == null || item.value === '') {
      return;
    }

    if (item.fieldName == SettingKeys.keyTheme) {
      this.themes.setThemeByName(item.value + '-theme');
      if (configure && !item.readOnly) {
        item.order = 1;
        item.caption = 'SETTINGS.THEME';
        item.selectionType = VmpSelectionType.Select;
        this.viewSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keyLanguage) {
      this.languages.setSelectedLanguageByName(item.value);
      if (configure && !item.readOnly) {
        item.order = 2;
        item.caption = 'SETTINGS.LANGUAGE';
        item.selectionType = VmpSelectionType.Select;
        this.viewSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keyToolBarPosition) {
      this.viewMode.setToolbarPositionByName(item.value);
      if (configure && !item.readOnly) {
        item.order = 3;
        item.caption = 'SETTINGS.TOOLBARPOSITION';
        item.selectionType = VmpSelectionType.Select;
        this.viewSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keySideBarCollapse) {
      this.viewMode.sideBarCollapse = item.value;
      if (configure && !item.readOnly) {
        item.order = 4;
        item.caption = 'SETTINGS.SIDEBARCOLLAPSE';
        this.viewSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keyTextTruncation) {
      this.viewMode.setTextTruncationByName(item.value);
      if (configure && !item.readOnly) {
        item.order = 5;
        item.caption = 'SETTINGS.TEXTTRUNCATION';
        item.selectionType = VmpSelectionType.Select;
        this.viewSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keyShowTooltips) {
      this.viewMode.showTooltips = item.value;
      if (configure && !item.readOnly) {
        item.order = 6;
        item.caption = 'SETTINGS.SHOWTOOLTIPS';
        this.viewSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keyOrderInNewTab) {
      this.orderSettings.openOrderInNewTab = item.value;
      if (configure && !item.readOnly) {
        item.order = 5;
        item.caption = 'SETTINGS.ORDERINNEWTAB';
        this.orderSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keySearchButtonInHeader) {
      this.viewMode.searchInHeader = item.value;
      if (configure && !item.readOnly) {
        item.order = 1;
        item.caption = 'SETTINGS.SEARCHINHEADER';
        this.searchSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keySearchDefaultMode) {
      this.searchSettings.setDefaultSearchModeByName(item.value);
      if (configure && !item.readOnly) {
        item.order = 2;
        item.caption = 'SETTINGS.SEARCHDEFAULTMODE';
        item.selectionType = VmpSelectionType.Select;
        this.searchSettingFields.push(item);
      }
      return;
    }

    if (item.fieldName == SettingKeys.keySearchWildCard) {
      this.searchSettings.isWildCard = item.value;
      if (configure && !item.readOnly) {
        item.order = 3;
        item.caption = 'SEARCH.WILDCARD';
        this.searchSettingFields.push(item);
      }
      return;
    }

    // admin fields - no display
    if (item.fieldName == SettingKeys.keyDefaultHelpVisible) {
      this.helpSettings.defaultHelpFileVisible = item.value;
      return;
    }
    if (item.fieldName == SettingKeys.keyCustomHelpFile) {
      this.helpSettings.customHelpFile = item.value;
      return;
    }
    if (item.fieldName == SettingKeys.keyCustomHelpTitle) {
      this.helpSettings.customHelpFileText = item.value;
      return;
    }
  }

  private valueChanged(item: ViewModelProperty) {
    if (item == null) {
      return;
    }
    // apply value
    this.applySettingItem(item, false);
    // save it
    const valuePair = new KeyValuePair();
    valuePair.key = item.fieldName;
    valuePair.value = item.value;
    this.settingsChanged.next(valuePair);
  }
}
